import { CELLARS, DUNGEON, NIGHTMARES, type EnemyKind, type NightmareType } from '@somnhold/content';
import { touchGrid } from '../grid.ts';
import { DIRS, hexRing, hexToWorld, type Hex } from '../hex.ts';
import { dist2, idiv, imax, isqrt, mulBp, withinRange } from '../math/fixed.ts';
import { rngChance, rngInt, rngWeighted } from '../math/rng.ts';
import type { BattleState, DungeonRoom, DungeonSpawn, Enemy, ExpArea, ExpDevice, Expedition, ExpItem, ExpWing, Hero } from '../types.ts';
import { damageEnemy, damageHero, ev, spawnEnemy } from './combat.ts';
import { stepToward } from './enemies.ts';
import { bigParty } from '../campaign/party.ts';

/*
 * Group expeditions (docs/EXPEDICIONES.md). The garrison cellars: down past the guard room to the great
 * floodgate; then each hero takes a wing, and each wing's second room is opened by another wing's first
 * (the tower's lever opens the cisterns, the cisterns' wheels drain the powder store, a barrel from the powder
 * store blows the rubble on the tower's stair); every wing's lock opens the floodgate, and the bell behind it
 * goes up the well on the winch while the ink rises. Several rooms fight at once: a nightmare never leaves the
 * room it was born in.
 */

const C = CELLARS;
const standing = (h: Hero): boolean => h.downTicks === 0;
const at = (h: Hex): { x: number; y: number } => hexToWorld(h.q, h.r);
const plus = (h: Hex, dir: number, k: number): Hex => {
  const d = DIRS[dir] as Hex;
  return { q: h.q + d.q * k, r: h.r + d.r * k };
};

const roomOf = (s: BattleState, i: number): DungeonRoom | undefined => s.map.dungeon?.rooms[i];

/** Inside a room's disc (a little past its last ring). */
export const inExpRoom = (room: DungeonRoom, x: number, y: number, margin = 0): boolean => {
  const c = at(room);
  const r = room.radius * 1000 + 450 - margin;
  return dist2(x, y, c.x, c.y) <= r * r;
};

/** The room a point stands in (-1: a corridor). */
export const expRoomAt = (s: BattleState, x: number, y: number): number => (s.map.dungeon?.rooms ?? []).findIndex((r) => inExpRoom(r, x, y));

const doorIndex = (x: Expedition, a: ExpArea, b: ExpArea): number => x.links.findIndex(([p, q]) => p === a && q === b);

const setDoor = (s: BattleState, x: Expedition, a: ExpArea, b: ExpArea, open: boolean): void => {
  const i = doorIndex(x, a, b);
  if (i < 0 || x.open[i] === open) return;
  x.open[i] = open;
  touchGrid(s);
  const d = s.map.dungeon?.doors[i];
  if (open && d) {
    const w = at(d);
    ev(s, { t: 'skill', hero: 0, skill: 'expDoor', x: w.x, y: w.y });
  }
};

/** Builds the cellars' state once the battle is laid out: devices where they belong, the bell behind the floodgate. */
export const newCellars = (s: BattleState, areas: ExpArea[], links: [ExpArea, ExpArea][], spawns: DungeonSpawn[]): Expedition => {
  const rooms = Object.fromEntries((Object.keys(C.rooms) as ExpArea[]).map((a) => [a, areas.indexOf(a)])) as Record<ExpArea, number>;
  const R = C.rooms;
  const D = C.device;
  const devices: ExpDevice[] = [];
  const dev = (kind: ExpDevice['kind'], area: ExpArea, h: Hex, need: number, wing?: ExpWing): void => {
    if (rooms[area] < 0) return;
    devices.push({ kind, room: rooms[area], q: h.q, r: h.r, hold: 0, need, done: false, ...(wing ? { wing } : {}) });
  };
  dev('wheel', 'cisternA', plus(R.cisternA, 1, 2), D.wheelTicks, 'cistern');
  dev('wheel', 'cisternA', plus(R.cisternA, 4, 2), D.wheelTicks, 'cistern');
  dev('lever', 'towerA', plus(R.towerA, 0, 2), D.leverTicks, 'tower');
  dev('barrels', 'powderA', plus(R.powderA, 3, 2), 0, 'powder');
  const rubble = s.map.dungeon?.doors[links.findIndex(([a, b]) => a === 'towerA' && b === 'towerB')];
  if (rubble) dev('rubble', 'towerA', rubble, 0, 'tower');
  dev('lock', 'cisternB', R.cisternB, D.lockTicks, 'cistern');
  dev('lock', 'towerB', R.towerB, D.lockTicks, 'tower');
  dev('lock', 'powderB', R.powderB, D.lockTicks, 'powder');
  // The cells' lock is on the floodgate itself: it opens when the key is laid by it.
  const gate = s.map.dungeon?.doors[links.findIndex(([a, b]) => a === 'hub' && b === 'cistern')];
  if (rooms.cells >= 0 && gate) dev('lock', 'hub', gate, 0, 'cells');
  dev('winch', 'hub', R.hub, C.winch.ticks);
  const items: ExpItem[] = [];
  const item = (kind: ExpItem['kind'], h: Hex, hp = 1): void => {
    const w = at(h);
    items.push({ id: s.nextId++, kind, x: w.x, y: w.y, hp, carriers: [], fuse: 0 });
  };
  if (rooms.cells >= 0) item('key', plus(R.cells, 1, 2));
  item('bell', plus(R.cistern, 5, 2));
  // Open from the start: the way down and every wing's first room. The rest opens as the cellars are worked.
  const open = links.map(([a, b]) => a === 'entry' || (a === 'hub' && b !== 'cistern'));
  return {
    kind: 'cellars',
    rooms,
    links,
    open,
    woke: areas.map((a) => a === 'entry' || a === 'hub'),
    guardWave: 0,
    guardAt: 0,
    devices,
    items,
    pressAt: areas.map(() => 0),
    gateOpen: false,
    inkAt: 0,
    falls: 0,
    downAt: s.heroes.map(() => 0),
    goneAt: s.heroes.map(() => 0),
    winchPresses: 0,
    raised: false,
    spawns,
  };
};

/** Is door `i` open? (grid.ts asks, to block heroes at a shut one.) */
export const expDoorOpen = (x: Expedition, i: number): boolean => x.open[i] ?? true;

/** Everything down here: spawned in a room, it stays in it. */
const spawnIn = (s: BattleState, room: number, kind: EnemyKind, tier: 1 | 2 | 3, elite: boolean, x: number, y: number, hpBp = 10000): Enemy => {
  const e = spawnEnemy(s, kind, tier, elite, 0, x, y);
  e.room = room;
  const d = s.dungeon;
  if (d) e.damage = mulBp(e.damage, d.dmgBp);
  if (hpBp !== 10000) e.hp = e.maxHp = mulBp(e.maxHp, hpBp);
  ev(s, { t: 'spawn', id: e.id });
  return e;
};

const wakeRoom = (s: BattleState, x: Expedition, i: number): void => {
  x.woke[i] = true;
  for (const sp of x.spawns) {
    if (sp.room !== i || sp.wave !== 0) continue;
    const p = at(sp);
    spawnIn(s, i, sp.kind, sp.tier, sp.elite, p.x, p.y, sp.hpBp);
  }
};

/** A few more from the edge of a room while someone works in it (and around the bell); `inkBp` of them inked, `hpBp` their life. */
const press = (s: BattleState, i: number, points: number, inkBp = 0, hpBp = 10000): void => {
  const room = roomOf(s, i);
  if (!room) return;
  const beasts = C.bestiary;
  const entries = (Object.keys(beasts) as NightmareType[]).sort().map((t) => [t, beasts[t] as number] as const);
  const ring = hexRing(room.radius).map((h) => ({ q: room.q + h.q, r: room.r + h.r }));
  // As many as there are heroes in the room to meet them.
  const here = s.heroes.filter((h) => standing(h) && inExpRoom(room, h.x, h.y)).length;
  let left = mulBp(points * (here || 1), s.dungeon?.dmgBp ?? 10000);
  let guard = 0;
  while (left > 0 && guard++ < 20) {
    const fit = entries.filter(([t]) => NIGHTMARES[t].points <= left);
    if (!fit.length) break;
    const kind = rngWeighted(s.rng, fit);
    left -= NIGHTMARES[kind].points;
    const p = at(ring[rngInt(s.rng, ring.length)] as Hex);
    spawnIn(s, i, kind, inkBp > 0 && rngChance(s.rng, inkBp) ? 2 : 1, false, p.x, p.y, hpBp);
  }
};

/** The winch turning: the press grows as the bell comes up, and now and then the old garrison's sergeants come with it. */
const winchPress = (s: BattleState, x: Expedition, i: number, winch: ExpDevice): void => {
  const W = C.winchPress;
  const [lo, hi] = W.points as [number, number];
  // Levels the party has over the cellars' own: tougher, and more sergeants.
  let lv = 0;
  for (const h of s.heroes) lv += h.level;
  const over = imax(0, idiv(lv, s.heroes.length || 1) - W.levelFrom);
  const hpBp = 10000 + over * W.perLevelHpBp;
  press(s, i, lo + idiv((hi - lo) * winch.hold, winch.need || 1), W.inkBp, hpBp);
  x.winchPresses++;
  if (x.winchPresses % W.eliteEvery !== 0) return;
  const room = roomOf(s, i);
  if (!room) return;
  const ring = hexRing(room.radius).map((h) => ({ q: room.q + h.q, r: room.r + h.r }));
  const n = (W.elites[s.heroes.length > 8 ? 8 : s.heroes.length] as number) + idiv(over, W.elitePerLevels);
  for (let k = 0; k < n; k++) {
    const p = at(ring[rngInt(s.rng, ring.length)] as Hex);
    spawnIn(s, i, C.guardElites.kind, 2, true, p.x, p.y, mulBp(W.eliteHpBp, hpBp));
  }
};

const enemiesIn = (s: BattleState, i: number): boolean => s.enemies.some((e) => e.hp > 0 && e.room === i);

/** The guard room fights like a crypt room: the way back shuts, two waves, then the way on opens. */
const guardTick = (s: BattleState, x: Expedition): void => {
  const g = x.rooms.guard;
  const room = roomOf(s, g);
  if (!room) return;
  if (!x.woke[g]) {
    if (!s.heroes.some((h) => standing(h) && inExpRoom(room, h.x, h.y, 700))) return;
    // Nobody fights alone: whoever lingered in the way down is pulled in as the door shuts behind them.
    const c = at(room);
    let k = 0;
    for (const h of s.heroes) {
      if (inExpRoom(room, h.x, h.y, 300)) continue;
      // A party past four: a second row, a step further in.
      h.x = h.destX = h.ax = k < 4 ? c.x + ((k % 3) - 1) * 500 : c.x + (k - 5) * 500 - 250;
      h.y = h.destY = h.ay = c.y - (room.radius - 1) * 866 + (k < 4 ? 0 : 600);
      h.moving = false;
      h.path = [];
      k++;
    }
    setDoor(s, x, 'entry', 'guard', false);
    wakeRoom(s, x, g);
    return;
  }
  if (x.open[doorIndex(x, 'guard', 'hub')]) return;
  if (enemiesIn(s, g)) return;
  if (x.guardWave === 0) {
    if (x.guardAt === 0) x.guardAt = s.tick + DUNGEON.waveGap;
    if (s.tick < x.guardAt) return;
    x.guardWave = 1;
    for (const sp of x.spawns) {
      if (sp.room !== g || sp.wave !== 1) continue;
      const p = at(sp);
      spawnIn(s, g, sp.kind, sp.tier, sp.elite, p.x, p.y, sp.hpBp);
    }
    return;
  }
  // Cleared: everyone catches their breath and the floodgate hall opens.
  setDoor(s, x, 'entry', 'guard', true);
  setDoor(s, x, 'guard', 'hub', true);
  for (const h of s.heroes) {
    if (!standing(h)) continue;
    h.hp += mulBp(h.stats.maxHp, DUNGEON.clearHealBp);
    if (h.hp > h.stats.maxHp) h.hp = h.stats.maxHp;
  }
};

const nearDevice = (h: Hero, d: ExpDevice, r: number = C.device.range): boolean => {
  const w = at(d);
  return withinRange(h.x, h.y, w.x, w.y, r);
};

/** Standing by a wheel, a lever, a lock or the winch works it (not with your hands full). */
const workDevices = (s: BattleState, x: Expedition): boolean[] => {
  const busy = x.pressAt.map(() => false);
  const bell = x.items.find((it) => it.kind === 'bell');
  for (const d of x.devices) {
    if (d.done || d.need === 0) continue;
    if (d.kind === 'winch' && !bell?.hooked) continue;
    const range = d.kind === 'winch' ? C.winch.range : C.device.range;
    if (!s.heroes.some((h) => standing(h) && h.carry === undefined && nearDevice(h, d, range))) continue;
    d.hold++;
    if (d.kind !== 'lock') busy[d.room] = true;
    if (d.hold < d.need) continue;
    d.done = true;
    const w = at(d);
    ev(s, { t: 'skill', hero: 0, skill: d.kind === 'lock' ? 'expLock' : d.kind === 'winch' ? 'expRaised' : d.kind === 'wheel' ? 'expWheel' : 'expLever', x: w.x, y: w.y });
    if (d.kind === 'winch') x.raised = true;
  }
  return busy;
};

/** What the devices open: the circle between the wings, and the floodgate once every lock is off. */
const consequences = (s: BattleState, x: Expedition): void => {
  const wheels = x.devices.filter((d) => d.kind === 'wheel');
  if (wheels.length && wheels.every((d) => d.done)) setDoor(s, x, 'powderA', 'powderB', true);
  if (x.devices.some((d) => d.kind === 'lever' && d.done)) setDoor(s, x, 'cisternA', 'cisternB', true);
  if (x.devices.some((d) => d.kind === 'rubble' && d.done)) setDoor(s, x, 'towerA', 'towerB', true);
  const locks = x.devices.filter((d) => d.kind === 'lock');
  if (!x.gateOpen && locks.every((d) => d.done)) {
    x.gateOpen = true;
    x.inkAt = s.tick + C.inkTicks - x.falls * C.fallInkTicks;
    setDoor(s, x, 'hub', 'cistern', true);
    const i = x.rooms.cistern;
    const room = roomOf(s, i);
    if (room) {
      // What sleeps beside the bell wakes as the water drains.
      const c = at(plus(room, 2, 2));
      spawnIn(s, i, C.guardian.kind, 2, true, c.x, c.y, bigParty(C.guardian.hpBp, s.heroes.length, C.big));
      wakeRoom(s, x, i);
    }
  }
};

const itemOf = (x: Expedition, id: number | undefined): ExpItem | undefined => (id === undefined ? undefined : x.items.find((it) => it.id === id));

const release = (x: Expedition, h: Hero): ExpItem | undefined => {
  const it = itemOf(x, h.carry);
  h.carry = undefined;
  if (it) it.carriers = it.carriers.filter((c) => c !== h.id);
  return it;
};

/** Put down what is in hand where it is: a barrel by the rubble lights, the key by the floodgate turns, the bell by the winch hangs. */
const putDown = (s: BattleState, x: Expedition, h: Hero): void => {
  const it = release(x, h);
  if (!it) return;
  if (it.kind === 'bell') {
    // Either carrier lays it on the hook, and both let go.
    const winch = x.devices.find((d) => d.kind === 'winch');
    if (!winch || !nearDevice(h, winch, C.winch.range)) return;
    for (const o of s.heroes) if (o.carry === it.id) o.carry = undefined;
    it.carriers = [];
    it.hooked = true;
    const w = at(winch);
    it.x = w.x;
    it.y = w.y;
    ev(s, { t: 'skill', hero: h.id, skill: 'expHooked', x: w.x, y: w.y });
    return;
  }
  if (it.kind === 'barrel') {
    const rubble = x.devices.find((d) => d.kind === 'rubble' && !d.done);
    if (rubble && nearDevice(h, rubble, C.barrel.rubbleRange)) {
      it.fuse = s.tick + C.barrel.fuse;
      ev(s, { t: 'skill', hero: h.id, skill: 'expFuse', x: it.x, y: it.y });
    }
  } else if (it.kind === 'key') {
    const lock = x.devices.find((d) => d.kind === 'lock' && d.wing === 'cells');
    if (lock && !lock.done && nearDevice(h, lock, C.barrel.rubbleRange)) {
      lock.done = true;
      x.items = x.items.filter((o) => o !== it);
      ev(s, { t: 'skill', hero: h.id, skill: 'expLock', x: it.x, y: it.y });
    }
  }
};

/** The carry command: put down what is in hand, or take what is at hand (a fresh barrel from the stack if none is out). */
export const expCarry = (s: BattleState, h: Hero): void => {
  const x = s.dungeon?.exp;
  if (!x || !standing(h)) return;
  if (h.carry !== undefined) {
    putDown(s, x, h);
    return;
  }
  let best: ExpItem | undefined;
  let bd = C.carry.reach * C.carry.reach + 1;
  for (const it of x.items) {
    if (it.hooked || it.fuse > 0 || it.carriers.length >= (it.kind === 'bell' ? 2 : 1)) continue;
    const dd = dist2(h.x, h.y, it.x, it.y);
    if (dd < bd) {
      bd = dd;
      best = it;
    }
  }
  if (!best) {
    const stack = x.devices.find((d) => d.kind === 'barrels');
    const rubble = x.devices.find((d) => d.kind === 'rubble');
    if (!stack || rubble?.done || !nearDevice(h, stack, C.carry.reach) || x.items.some((it) => it.kind === 'barrel')) return;
    best = { id: s.nextId++, kind: 'barrel', x: h.x, y: h.y, hp: C.barrel.hp, carriers: [], fuse: 0 };
    x.items.push(best);
  }
  best.carriers.push(h.id);
  h.carry = best.id;
  h.target = 0;
  ev(s, { t: 'skill', hero: h.id, skill: 'expPick', x: best.x, y: best.y });
};

/** A carrier's pace: slower with a load, slowest under the bell. */
export const carryBp = (s: BattleState, h: Hero): number => {
  const it = itemOf(s.dungeon?.exp as Expedition, h.carry);
  if (!it) return 10000;
  return it.kind === 'bell' ? C.carry.bellSpeedBp : C.carry.speedBp;
};

const burst = (s: BattleState, x: Expedition, it: ExpItem): void => {
  x.items = x.items.filter((o) => o !== it);
  for (const h of s.heroes) if (h.carry === it.id) h.carry = undefined;
  const B = C.barrel;
  ev(s, { t: 'boom', x: it.x, y: it.y, r: B.blastRadius, kind: 'barrel' });
  for (const h of s.heroes) if (standing(h) && withinRange(h.x, h.y, it.x, it.y, B.blastRadius)) damageHero(s, h, B.blastDamage, null, 'fire');
  for (const e of s.enemies) if (e.hp > 0 && withinRange(e.x, e.y, it.x, it.y, B.blastRadius)) damageEnemy(s, e, B.blastDamage * 3, { pid: '', ignoresArmor: true, type: 'fire' });
  const rubble = x.devices.find((d) => d.kind === 'rubble' && !d.done);
  if (rubble) {
    const w = at(rubble);
    if (withinRange(w.x, w.y, it.x, it.y, B.blastRadius)) {
      rubble.done = true;
      ev(s, { t: 'boom', x: w.x, y: w.y, r: B.blastRadius, kind: 'rubble' });
    }
  }
};

/** Loads move with their carriers; the bell only with two, and it keeps them close. */
const carryTick = (s: BattleState, x: Expedition): void => {
  for (const it of [...x.items]) {
    if (it.kind === 'barrel' && (it.hp <= 0 || (it.fuse > 0 && s.tick >= it.fuse))) {
      burst(s, x, it);
      continue;
    }
    const hs = it.carriers.map((id) => s.heroes.find((h) => h.id === id)).filter((h): h is Hero => !!h);
    if (!hs.length) continue;
    if (it.kind !== 'bell') {
      const h = hs[0] as Hero;
      it.x = h.x;
      it.y = h.y;
      continue;
    }
    if (hs.length >= 2) {
      let sx = 0;
      let sy = 0;
      for (const h of hs) {
        sx += h.x;
        sy += h.y;
      }
      const tx = idiv(sx, hs.length);
      const ty = idiv(sy, hs.length);
      const step = idiv(mulBp(hs[0]?.stats.speed ?? 1500, C.carry.bellSpeedBp), 12);
      const o = { x: it.x, y: it.y, facing: 0 };
      stepToward(o, tx, ty, step);
      it.x = o.x;
      it.y = o.y;
    }
    // Under the bell a hero can't stray from it.
    const L = C.carry.bellLeash;
    for (const h of hs) {
      const d2 = dist2(h.x, h.y, it.x, it.y);
      if (d2 <= L * L) continue;
      const d = isqrt(d2) || 1;
      h.x = it.x + idiv((h.x - it.x) * L, d);
      h.y = it.y + idiv((h.y - it.y) * L, d);
    }
  }
};

/** Past the guard room: from then on a hero fallen alone comes to at the floodgate (before, as in the crypt, only an ally raises them). */
export const expApart = (x: Expedition): boolean => x.open[doorIndex(x, 'guard', 'hub')] ?? false;

/** Fallen heroes: what they carried drops; alone too long, they come to at the floodgate. Every fall feeds the ink. */
const fallTick = (s: BattleState, x: Expedition): void => {
  const hub = roomOf(s, x.rooms.hub);
  s.heroes.forEach((h, i) => {
    if (h.downTicks === 0) {
      x.downAt[i] = 0;
      return;
    }
    if (x.downAt[i] === 0) {
      x.downAt[i] = s.tick;
      x.falls++;
      if (x.gateOpen) x.inkAt -= C.fallInkTicks;
      release(x, h);
      return;
    }
    if (s.tick - (x.downAt[i] as number) < C.downTicks || !hub || !expApart(x)) return;
    if (s.heroes.every((o) => !standing(o))) return;
    const c = at(hub);
    h.x = h.destX = h.ax = c.x + ((i % 3) - 1) * 500;
    h.y = h.destY = h.ay = c.y - 900;
    h.path = [];
    h.moving = false;
    h.downTicks = 0;
    h.revive = 0;
    h.hp = mulBp(h.stats.maxHp, C.respawnHpBp) || 1;
    h.anim = 'idle';
    h.invuln = 12;
    x.downAt[i] = 0;
    ev(s, { t: 'up', id: h.id });
  });
};

/** A dropped player has a while to come back; after that the expedition is lost. */
const goneTick = (s: BattleState, x: Expedition): void => {
  s.heroes.forEach((h, i) => {
    if (h.control !== 'ghost') x.goneAt[i] = 0;
    else if (x.goneAt[i] === 0) x.goneAt[i] = s.tick;
    else if (s.tick - (x.goneAt[i] as number) >= C.dropGraceTicks) x.lost = 'gone';
  });
};

/** One tick of an expedition, before heroes and nightmares act. */
export const expeditionTick = (s: BattleState, x: Expedition): void => {
  // Walking into a room wakes what is in it.
  x.woke.forEach((w, i) => {
    if (w || i === x.rooms.guard || i === x.rooms.cistern) return;
    const room = roomOf(s, i);
    if (room && s.heroes.some((h) => standing(h) && inExpRoom(room, h.x, h.y, 700))) wakeRoom(s, x, i);
  });
  guardTick(s, x);
  const busy = workDevices(s, x);
  // The bell on the move draws them too.
  const bell = x.items.find((it) => it.kind === 'bell');
  if (bell && bell.carriers.length && !bell.hooked) {
    const i = expRoomAt(s, bell.x, bell.y);
    if (i >= 0) busy[i] = true;
  }
  const winch = bell?.hooked ? x.devices.find((d) => d.kind === 'winch' && !d.done) : undefined;
  busy.forEach((b, i) => {
    if (!b) {
      x.pressAt[i] = 0;
      return;
    }
    const cranking = !!winch && winch.room === i;
    const every = cranking ? C.winchPress.every : C.pressEvery;
    if (x.pressAt[i] === 0) x.pressAt[i] = s.tick + every;
    else if (s.tick >= (x.pressAt[i] as number)) {
      x.pressAt[i] = s.tick + every;
      if (cranking) winchPress(s, x, i, winch);
      else press(s, i, i === x.rooms.cistern || i === x.rooms.hub ? C.points.final : C.points.press);
    }
  });
  consequences(s, x);
  carryTick(s, x);
  fallTick(s, x);
  goneTick(s, x);
  if (x.gateOpen && !x.raised && s.tick >= x.inkAt) x.lost = 'ink';
};

/** A nightmare of the cellars: after the nearest standing hero in its own room; it never leaves the room. */
export const expChase = (s: BattleState, e: Enemy, px: number, attack: (h: Hero) => void): boolean => {
  const room = e.room === undefined ? undefined : roomOf(s, e.room);
  if (!room) return false;
  let tgt: Hero | null = null;
  let bd = 0x7fffffff;
  for (const h of s.heroes) {
    if (!standing(h) || !inExpRoom(room, h.x, h.y, -600)) continue;
    const dd = dist2(e.x, e.y, h.x, h.y);
    if (e.taunter === h.id) {
      tgt = h;
      break;
    }
    if (dd < bd) {
      bd = dd;
      tgt = h;
    }
  }
  if (!tgt) {
    e.anim = 'walk';
    return true;
  }
  e.tgtKind = 1;
  e.tgtId = tgt.id;
  if (withinRange(e.x, e.y, tgt.x, tgt.y, NIGHTMARES[e.kind].reach + 150)) attack(tgt);
  else {
    e.anim = 'walk';
    stepToward(e, tgt.x, tgt.y, px);
  }
  // Clamp to its room.
  const c = at(room);
  const r = room.radius * 1000;
  const d2 = dist2(e.x, e.y, c.x, c.y);
  if (d2 > r * r) {
    const d = isqrt(d2) || 1;
    e.x = c.x + idiv((e.x - c.x) * r, d);
    e.y = c.y + idiv((e.y - c.y) * r, d);
  }
  return true;
};
